import { useEffect, useState } from 'react'
import { vmhaRiskDoc } from 'app/docLinks'
import Alert from 'core/components/Alert'
import LearnMoreLink from 'core/components/LearnMoreLink'
import Button from 'core/elements/button'
import Checkbox from 'core/elements/input/Checkbox'
import Modal from 'core/elements/modal/Modal'
import Text from 'core/elements/Text'
import { useStyles } from './CreateClusterHostGroupsModal'

interface Props {
  open: boolean
  onCancel: () => void
  onConfirm: () => void
  allConditionsMet?: boolean
  loading?: boolean
  error?: { title?: string; message: string } | null
}

const VMHAConfirmationDialog = ({
  open,
  onCancel,
  onConfirm,
  allConditionsMet = false,
  loading = false,
  error = null,
}: Props) => {
  const classes = useStyles()
  const [confirmVMHARiskModalChecked, setConfirmVMHARiskModalChecked] = useState(false)

  // Reset checkbox state when dialog opens
  useEffect(() => {
    if (open) {
      setConfirmVMHARiskModalChecked(false)
    }
  }, [open])

  return (
    <Modal
      panel="dialog"
      open={open}
      title={allConditionsMet ? 'Enable VM High Availability' : 'VM High Availability Risk'}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            loading={loading}
            disabled={allConditionsMet ? false : !confirmVMHARiskModalChecked || loading}
          >
            Confirm
          </Button>
        </>
      }
      onClose={onCancel}
    >
      {/*  If all conditions are met, show the standard confirmation modal content */}
      {allConditionsMet ? (
        <div className={classes.textContainer}>
          <Text variant="body2">
            All prerequisites are met. VM HA helps protect your workloads by automatically
            restarting VMs on another host if a failure occurs.
          </Text>
          <Text variant="body2">Confirm to enable this protection for your cluster.</Text>
        </div>
      ) : (
        /* If the image library is degraded, show the VMHA risk confirmation modal */
        <div className={classes.vmhaRiskModalText}>
          <Text variant="body2">
            The cluster has only one Image Library host available. The cluster will reach{' '}
            <b>Protected</b> status only when all prerequisites are satisfied.
          </Text>
          <Text variant="body2">
            Partial or unmet conditions will result in <b>Not Protected</b> or <b>Degraded</b> VMHA
            status. <LearnMoreLink url={vmhaRiskDoc} />
          </Text>
          <Checkbox
            checked={confirmVMHARiskModalChecked}
            onChange={(value) => setConfirmVMHARiskModalChecked(value)}
            label={<Text variant="body2">I understand the risks.</Text>}
          />
        </div>
      )}
      {/* Error Message */}
      {error && (
        <Alert variant="error">
          <Text variant="body2">{error?.message}</Text>
        </Alert>
      )}
    </Modal>
  )
}

export default VMHAConfirmationDialog
